import { useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import Heading from "../components/heading";
import MarkdownDisplay from "../components/markdownDisplay.jsx";
import PageHero from "../components/pageHero.jsx";
import { JobsContext } from "../context/JobsContext";
import { useLocalePath } from "../hooks/useLocalePath";
import {
  absoluteUrl,
  DEFAULT_COMPANY_NAME,
  LOGO_URL,
  ORGANIZATION_ID,
  SITE_ORIGIN,
} from "../seoUtils";

const JobDescription = () => {
  const { id } = useParams();
  const { jobs } = useContext(JobsContext);
  const { locale, localePath } = useLocalePath();
  // Derived at render, not mirrored into state via an effect: effects never run during the
  // server render, so an effect-populated `job` left this page empty for crawlers.
  const job = jobs.find((j) => j.slug === id) || null;

  return (
    job && (
      <div>
        <Helmet>
          <title>AS Maskiner - Jobb - {job.title}</title>
          <meta
            name="description"
            content={`Sök till oss på AS Maskiner för rollen som ${job.title} i ${job.location}. Bli del av ett team som värdesätter innovation och flexibilitet.`}
          />
          <meta
            name="keywords"
            content={`jobb, ${job.title}, ${job.location}, AS Maskiner, lediga jobb, karriär, jobbmöjligheter, CNC maskiner, snickerimaskiner`}
          />
          <meta
            property="og:url"
            content={absoluteUrl(locale, `/jobs/${encodeURIComponent(job.slug)}`)}
          />
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "JobPosting",
              title: job.title,
              description: job.teaser || job.body || job.title,
              url: absoluteUrl(locale, `/jobs/${encodeURIComponent(job.slug)}`),
              ...(job.createdAt ? { datePosted: new Date(job.createdAt).toISOString() } : {}),
              ...(job.workType ? { employmentType: job.workType } : {}),
              hiringOrganization: {
                "@type": "Organization",
                "@id": ORGANIZATION_ID,
                name: DEFAULT_COMPANY_NAME,
                sameAs: SITE_ORIGIN,
                logo: LOGO_URL,
              },
              jobLocation: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  ...(job.location ? { addressLocality: job.location } : {}),
                  addressCountry: "SE",
                },
              },
            })}
          </script>
        </Helmet>
        <PageHero
          titleKey="careers.hero_title"
          titleFallback="Karriär"
          subtitleKey="careers.hero_subtitle"
          backgroundKey="careers.hero_background"
          alignment="bottom"
        />
        <div className="container mx-auto max-w-7xl p-8">
          <div className="mb-4">
            <Link to={localePath("/jobs")} className="font-bold">
              ← Alla lediga jobb
            </Link>
          </div>
          <Heading sx="mb-4 w-fit" type="h1">
            {job.title}
          </Heading>
          {job.body ? <MarkdownDisplay data={job.body} /> : null}
        </div>
      </div>
    )
  );
};

export default JobDescription;
